import moment from "moment";
import { ReservationOccurrence, Reservation, ReservationType } from "src/types";
import { findDateInMonth } from "src/Utils/CalculateDates";

const VALID_FREQUENCIES = new Set(["daily", "weekly", "monthly", "yearly"]);

const isValidFrequency = (freq?: string | null): freq is "daily" | "weekly" | "monthly" | "yearly" => {
  return !!freq && VALID_FREQUENCIES.has(freq);
};

const isValidDateStr = (d?: string | null): boolean => !!d && moment(d, "YYYY-MM-DD", true).isValid();

const normalizeDowToMomentWeekday = (dow: number): number => {
  // Input can be 0..7 where 0 and 7 are Sunday. Moment weekday() is 0..6 where 0 is Sunday.
  if (dow === 7) return 0;
  if (dow === 0) return 0;
  if (dow >= 1 && dow <= 6) return dow;
  return 0;
};

const normalizeDowToIsoWeekday = (dow: number): number => {
  // findDateInMonth expects 1..7 where 7 is Sunday.
  if (dow === 0) return 7;
  if (dow === 7) return 7;
  if (dow >= 1 && dow <= 6) return dow;
  return 7;
};

const dayBefore = (dateStr: string): string => moment(dateStr, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD");

const asIntArray = (value: unknown): number[] => {
  if (Array.isArray(value)) return value.map(v => Number(v)).filter(v => Number.isFinite(v));
  if (value === null || value === undefined) return [];
  const n = Number(value);
  return Number.isFinite(n) ? [n] : [];
};

const coerceRecurringDates = (r: Reservation): number[] => {
  const anyR = r as any;
  // Prefer explicit "dates" if present; else use recurring_date.
  const dates = asIntArray(anyR?.dates);
  if (dates.length > 0) return dates;
  const fromRecurringDate = asIntArray(anyR?.recurring_date);
  if (fromRecurringDate.length > 0) return fromRecurringDate;
  // No explicit dates.
  return [];
};

const coerceWeekNumbers = (r: Reservation): number[] => {
  const anyR = r as any;
  const weekNumbers = asIntArray(anyR?.week_numbers);
  if (weekNumbers.length > 0) return weekNumbers;
  const single = asIntArray(anyR?.week_number);
  return single;
};

const coerceMonthDows = (r: Reservation): number[] => {
  // For monthly/yearly, support days_of_week array (0..7) or day_of_week single.
  const anyR = r as any;
  const dows = asIntArray(anyR?.days_of_week);
  if (dows.length > 0) return dows;
  const single = asIntArray(anyR?.day_of_week);
  return single;
};

// Convert reservations to calendar events
export const createScheduleEvents = (reservations: Reservation[], currentDate: Date, types: ReservationType[]): ReservationOccurrence[] => {
  // On mobile, when looking at a month, we use agenda view, which shouldn't have extra days shown
  const calendarBufferDays = 7;

  // Calculate view range (start and end dates)
  const viewStartDate = moment(currentDate).startOf("month").subtract(calendarBufferDays, "days").format("YYYY-MM-DD");
  const viewEndDate = moment(currentDate).endOf("month").add(calendarBufferDays, "days").format("YYYY-MM-DD");

  // Build parent/child graph
  const byId = new Map<number, Reservation>();
  for (const r of reservations) {
    if (typeof r.id === "number") byId.set(r.id, r);
  }

  const childrenByParent = new Map<number, Reservation[]>();
  for (const r of reservations) {
    if (typeof r.parent_id === "number") {
      const parent = byId.get(r.parent_id);
      if (!parent) {
        console.warn(`[createScheduleEvents] Child reservation ${r.id ?? "(no id)"} ignored: parent ${r.parent_id} not found.`);
        continue;
      }
      const arr = childrenByParent.get(r.parent_id) ?? [];
      arr.push(r);
      childrenByParent.set(r.parent_id, arr);
    }
  }

  const allEvents: Reservation[] = [];

  for (const reservation of reservations) {
    // Only process roots (no parent).
    if (typeof reservation.parent_id === "number") continue;

    const children = typeof reservation.id === "number" ? (childrenByParent.get(reservation.id) ?? []) : [];
    const isRecurring = isValidFrequency(reservation.recurring_frequency);

    if (!isRecurring) {
      if (children.length > 0) {
        console.warn(
          `[createScheduleEvents] Reservation ${reservation.id ?? "(no id)"} has children but is not recurring; children ignored.`
        );
      }
      // Only include non-recurring reservations that overlap the view range.
      const rStart = isValidDateStr(reservation.start_date) ? moment(reservation.start_date, "YYYY-MM-DD") : null;
      const rEnd = isValidDateStr(reservation.end_date) ? moment(reservation.end_date, "YYYY-MM-DD") : rStart;
      if (rStart && rEnd) {
        const overlaps =
          rStart.isSameOrBefore(moment(viewEndDate, "YYYY-MM-DD"), "day") && rEnd.isSameOrAfter(moment(viewStartDate, "YYYY-MM-DD"), "day");
        if (overlaps) allEvents.push(reservation);
      }
      continue;
    }

    allEvents.push(...expandRecurringReservationWithChildren(reservation, children, viewStartDate, viewEndDate));
  }

  // Convert all events to calendar format
  return allEvents.map(reservation => {
    // Format start and end times
    const startDate = moment(`${reservation.start_date} ${reservation.start_time}`).toDate();
    const endDate = moment(`${reservation.end_date} ${reservation.end_time}`).toDate();

    // Create title with recurring indicator
    let title = reservation.title || reservation.description || "";

    const type = reservation.type_id ? types.find(type => type.id === reservation.type_id) : null;
    const colorCustomizable = Boolean(type?.color_customizable);
    const hexColor = colorCustomizable ? reservation?.color || type?.color || "white" : type?.color || "white";

    return {
      startDate: startDate,
      endDate: endDate,
      startTime: reservation.start_time,
      endTime: reservation.end_time,
      title: title,
      opacity: 1,
      hexColor: hexColor,
      reservation: reservation.originalReservation || reservation, // Keep the original reservation for recurring events
      isRecurring: !!reservation.recurring_frequency || !!reservation.isRecurring,
      type: reservation.type_id,
      allDay: false,
      isPrivate: reservation.is_private,
      isPlaceholder: reservation.privacy_placeholder || false
    };
  });
};

// Expand a recurring reservation by applying any children modifications.
// Recurring children are applied first (sorted by start_date), then non-recurring children move/modify
// individual occurrences.
const expandRecurringReservationWithChildren = (
  parent: Reservation,
  children: Reservation[],
  viewStartDate: string,
  viewEndDate: string
): Reservation[] => {
  if (!isValidFrequency(parent.recurring_frequency)) return [];

  const recurringChildren = children
    .filter(c => isValidFrequency(c.recurring_frequency))
    .filter(c => {
      if (!isValidDateStr(c.start_date)) {
        console.warn(`[createScheduleEvents] Recurring child ${c.id ?? "(no id)"} ignored: invalid start_date.`);
        return false;
      }
      if (moment(c.start_date, "YYYY-MM-DD").isBefore(moment(parent.start_date, "YYYY-MM-DD"), "day")) {
        console.warn(`[createScheduleEvents] Recurring child ${c.id ?? "(no id)"} ignored: start_date before parent start_date.`);
        return false;
      }
      return true;
    })
    .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)));

  // Non-recurring children have a falsy recurring_frequency.
  // Children with a truthy but invalid recurring_frequency are ignored with a warning.
  const nonRecurringChildren = children.filter(child => !child.recurring_frequency);
  for (const child of children) {
    if (child.recurring_frequency && !isValidFrequency(child.recurring_frequency)) {
      console.warn(
        `[createScheduleEvents] Child reservation ${child.id ?? "(no id)"} ignored: invalid recurring_frequency '${child.recurring_frequency}'.`
      );
    }
  }

  // Build the segment templates (parent then recurring children).
  const segments: Reservation[] = [];
  const firstChildStart = recurringChildren[0]?.start_date;

  if (firstChildStart && isValidDateStr(firstChildStart)) {
    const parentSegEnd = dayBefore(firstChildStart);
    const parentEnd = isValidDateStr(parent.end_date) ? parent.end_date : null;
    const effectiveParentEnd = parentEnd ? (moment(parentEnd).isBefore(moment(parentSegEnd)) ? parentEnd : parentSegEnd) : parentSegEnd;
    if (!moment(effectiveParentEnd).isBefore(moment(parent.start_date), "day")) {
      segments.push({ ...parent, end_date: effectiveParentEnd });
    }
  } else {
    segments.push({ ...parent });
  }

  for (let i = 0; i < recurringChildren.length; i++) {
    const child = recurringChildren[i];
    const nextStart = recurringChildren[i + 1]?.start_date;
    const nextSegEnd = nextStart && isValidDateStr(nextStart) ? dayBefore(nextStart) : null;
    const childEnd = isValidDateStr(child.end_date) ? child.end_date : null;

    let effectiveEnd: string;
    if (nextSegEnd && childEnd) {
      effectiveEnd = moment(childEnd).isBefore(moment(nextSegEnd), "day") ? childEnd : nextSegEnd;
    } else if (nextSegEnd) {
      effectiveEnd = nextSegEnd;
    } else if (childEnd) {
      effectiveEnd = childEnd;
    } else {
      // end 20 years from today if no end specified
      effectiveEnd = moment().add(20, "years").format("YYYY-MM-DD");
    }

    if (!moment(effectiveEnd).isBefore(moment(child.start_date), "day")) {
      segments.push({ ...child, end_date: effectiveEnd });
    }
  }

  // Generate occurrences from each segment.
  let occurrences: Reservation[] = [];
  for (const seg of segments) {
    occurrences = occurrences.concat(generateRecurringEvents(seg, viewStartDate, viewEndDate));
  }

  // Apply non-recurring children (single-occurrence modifications).
  const moved = new Set<string>(); // parent_start_date strings already modified
  const byStartDate = new Map<string, number>();
  occurrences.forEach((o, idx) => {
    byStartDate.set(o.start_date, idx);
  });

  for (const child of nonRecurringChildren) {
    const parentStartDate = child?.parent_start_date;
    if (!parentStartDate || !isValidDateStr(parentStartDate)) {
      // Not a valid non-recurring modifier date
      continue;
    }
    if (moved.has(parentStartDate)) {
      console.warn(`[createScheduleEvents] Non-recurring child ${child.id ?? "(no id)"} ignored: occurrence ${parentStartDate} already modified.`);
      continue;
    }
    const idx = byStartDate.get(parentStartDate);
    if (idx === undefined) {
      console.warn(`[createScheduleEvents] Non-recurring child ${child.id ?? "(no id)"} parent occurrence ${parentStartDate} not found in generated range. Still included.`);
    }

    moved.add(parentStartDate);

    const target = idx !== undefined ? occurrences[idx] : null;

    const newStart = isValidDateStr(child.start_date) ? child.start_date : target ? target.start_date : null;
    if (!newStart) {
      console.warn(`[createScheduleEvents] Non-recurring child ${child.id ?? "(no id)"} ignored: cannot determine new start_date.`);
      continue;
    }

    const newEnd = isValidDateStr(child.end_date) ? child.end_date : newStart;

    const exceptionOccurrence: Reservation = {
      ...(target ?? {}),
      ...child,
      // This is a single exception occurrence, but still part of the series conceptually
      recurring_frequency: null,
      isRecurring: true,
      start_date: newStart,
      end_date: newEnd,
      originalReservation: parent
    } as Reservation;

    if (idx !== undefined) {
      // true modification/replacement
      occurrences[idx] = exceptionOccurrence;
    } else {
      // addition
      occurrences.push(exceptionOccurrence);
      // so future children can find it by its date
      byStartDate.set(exceptionOccurrence.start_date, occurrences.length - 1);
    }
  }

  // Re-sort in case non-recurring children moved dates.
  occurrences.sort((a, b) => {
    const da = moment(`${a.start_date} ${a.start_time}`);
    const db = moment(`${b.start_date} ${b.start_time}`);
    return da.diff(db);
  });

  return occurrences;
};

// Generate recurring events based on frequency and date range
const generateRecurringEvents = (reservation: Reservation, startOfViewRange: string, endOfViewRange: string): Reservation[] => {
  const events: Reservation[] = [];

  if (!isValidFrequency(reservation.recurring_frequency)) return events;
  const frequency = reservation.recurring_frequency;

  const recurringGap = reservation.recurring_gap && reservation.recurring_gap > 0 ? Math.floor(reservation.recurring_gap) : 1;

  const viewStart = moment(startOfViewRange, "YYYY-MM-DD").startOf("day");
  const viewEnd = moment(endOfViewRange, "YYYY-MM-DD").endOf("day");

  const startDate = moment(reservation.start_date, "YYYY-MM-DD").startOf("day");
  const endDate = isValidDateStr(reservation.end_date) ? moment(reservation.end_date, "YYYY-MM-DD").endOf("day") : null; // null => endless

  const startTime = moment(reservation.start_time, ["HH:mm:ss", "HH:mm"], true).isValid()
    ? moment(reservation.start_time, ["HH:mm:ss", "HH:mm"], true).format("HH:mm")
    : "00:00";
  const endTime = moment(reservation.end_time, ["HH:mm:ss", "HH:mm"], true).isValid()
    ? moment(reservation.end_time, ["HH:mm:ss", "HH:mm"], true).format("HH:mm")
    : startTime;

  const withinEnd = (d: moment.Moment): boolean => {
    if (d.isBefore(startDate, "day")) return false;
    if (d.isAfter(viewEnd, "day")) return false;
    if (d.isBefore(viewStart, "day")) return false;
    if (endDate && d.isAfter(endDate, "day")) return false;
    return true;
  };

  const pushOccurrence = (d: moment.Moment) => {
    if (!withinEnd(d)) return;
    events.push({
      ...reservation,
      start_date: d.format("YYYY-MM-DD"),
      end_date: d.format("YYYY-MM-DD"),
      start_time: startTime,
      end_time: endTime,
      isRecurring: true,
      originalReservation: reservation
    });
  };

  // --- Daily ----------------------------------------------------------------
  if (frequency === "daily") {
    let cur = startDate.clone();
    // Jump close to viewStart.
    if (cur.isBefore(viewStart, "day")) {
      const diffDays = viewStart.diff(cur, "days");
      const steps = Math.floor(diffDays / recurringGap);
      cur.add(steps * recurringGap, "days");
      while (cur.isBefore(viewStart, "day")) cur.add(recurringGap, "days");
    }
    while (cur.isSameOrBefore(viewEnd, "day")) {
      if (!endDate || cur.isSameOrBefore(endDate, "day")) pushOccurrence(cur);
      if (endDate && cur.isAfter(endDate, "day")) break;
      cur.add(recurringGap, "days");
    }
    return events;
  }

  // --- Weekly ---------------------------------------------------------------
  if (frequency === "weekly") {
    const anyR = reservation as any;
    const inputDays = Array.isArray(anyR?.days_of_week) ? (anyR.days_of_week as number[]) : [];
    const days = (inputDays.length > 0 ? inputDays : [normalizeDowToMomentWeekday(startDate.weekday())])
      .map(normalizeDowToMomentWeekday)
      .sort((a, b) => a - b);

    // If start_date is mid-week, the first occurrence for each day-of-week is the
    // first instance of that day on/after start_date. Then recurring_gap applies in weeks from
    // each day's first occurrence.
    for (const dow of days) {
      let first = startDate.clone().weekday(dow);
      if (first.isBefore(startDate, "day")) first.add(1, "week");

      // Jump close to viewStart
      if (first.isBefore(viewStart, "day")) {
        const weeksDiff = viewStart.diff(first, "weeks");
        const steps = Math.floor(weeksDiff / recurringGap);
        first.add(steps * recurringGap, "weeks");
        while (first.isBefore(viewStart, "day")) first.add(recurringGap, "weeks");
      }

      let cur = first;
      while (cur.isSameOrBefore(viewEnd, "day")) {
        if (!endDate || cur.isSameOrBefore(endDate, "day")) pushOccurrence(cur);
        if (endDate && cur.isAfter(endDate, "day")) break;
        cur = cur.clone().add(recurringGap, "weeks");
      }
    }

    // De-dupe and sort (e.g., if days_of_week contains both 0 and 7 for Sunday)
    const uniq = new Map<string, Reservation>();
    for (const e of events) uniq.set(e.start_date, e);
    return Array.from(uniq.values()).sort((a, b) => moment(a.start_date).diff(moment(b.start_date)));
  }

  // --- Monthly / Yearly -----------------------------------------------------
  const explicitDates = coerceRecurringDates(reservation); // [] if none
  const weekNumbers = coerceWeekNumbers(reservation);
  const monthDows = coerceMonthDows(reservation);
  const hasWeekdayPattern = weekNumbers.length > 0 && monthDows.length > 0;

  if (frequency === "monthly") {
    const datesToUse = explicitDates.length > 0 ? explicitDates : !hasWeekdayPattern ? [startDate.date()] : [];

    // Iterate months in steps of recurringGap.
    let curMonth = startDate.clone().startOf("month");
    if (curMonth.isBefore(viewStart, "month")) {
      const monthsDiff = viewStart.clone().startOf("month").diff(curMonth, "months");
      const steps = Math.floor(monthsDiff / recurringGap);
      curMonth.add(steps * recurringGap, "months");
      while (curMonth.isBefore(viewStart, "month")) curMonth.add(recurringGap, "months");
    }
    while (curMonth.isSameOrBefore(viewEnd, "month")) {
      if (hasWeekdayPattern && explicitDates.length === 0) {
        // Weekday pattern (e.g., 2nd Tue, last Thu)
        for (const wn of weekNumbers) {
          for (const dow of monthDows) {
            try {
              const target = findDateInMonth(curMonth.clone(), wn, normalizeDowToIsoWeekday(dow));
              if (target.isSameOrAfter(startDate, "day") && (!endDate || target.isSameOrBefore(endDate, "day"))) {
                pushOccurrence(target);
              }
            } catch (e) {
              // Skip invalid (e.g. 5th Monday doesn't exist)
            }
          }
        }
      } else {
        // Date-of-month pattern
        const uniqueDates = Array.from(new Set(datesToUse)).sort((a, b) => a - b);
        for (const d of uniqueDates) {
          const target = curMonth.clone().date(d);
          if (target.date() !== d) continue; // invalid date (e.g., Feb 30)
          if (target.isBefore(startDate, "day")) continue;
          if (endDate && target.isAfter(endDate, "day")) continue;
          pushOccurrence(target);
        }
      }

      curMonth.add(recurringGap, "months");
    }
    return events;
  }

  // Yearly
  if (frequency === "yearly") {
    const anyR = reservation as any;
    const monthIdx = typeof anyR?.recurring_month === "number" ? anyR.recurring_month - 1 : startDate.month();
    const usesWeekday = hasWeekdayPattern;

    let curYear = startDate.clone().year(viewStart.year()).startOf("year");
    if (curYear.isAfter(viewEnd, "year")) return events;

    // Start from the year that could first intersect the view.
    if (curYear.isBefore(viewStart, "year")) curYear = viewStart.clone().startOf("year");

    while (curYear.isSameOrBefore(viewEnd, "year")) {
      if (usesWeekday) {
        const baseMonth = curYear.clone().month(monthIdx).startOf("month");
        for (const wn of weekNumbers) {
          for (const dow of monthDows) {
            try {
              const target = findDateInMonth(baseMonth.clone(), wn, normalizeDowToIsoWeekday(dow));
              if (target.isBefore(startDate, "day")) continue;
              if (endDate && target.isAfter(endDate, "day")) continue;
              pushOccurrence(target);
            } catch (e) {
              // skip
            }
          }
        }
      } else {
        // Date-of-year (month/day) using start_date if weekday pattern absent.
        const target = curYear.clone().month(startDate.month()).date(startDate.date());
        if (target.month() === startDate.month() && target.date() === startDate.date()) {
          if (target.isSameOrAfter(startDate, "day") && (!endDate || target.isSameOrBefore(endDate, "day"))) {
            pushOccurrence(target);
          }
        }
      }
      curYear.add(recurringGap, "years");
    }

    return events;
  }

  return events;
};
